import { useState, useRef, useEffect, useCallback } from 'react'
import { Box, Input } from '@chakra-ui/react'
import { colors } from '../../utilities/colors'

function useDebounce(fn, delay) {
  const timer = useRef(null)
  useEffect(() => () => clearTimeout(timer.current), [])
  return useCallback(
    (...args) => {
      clearTimeout(timer.current)
      timer.current = setTimeout(() => fn(...args), delay)
    },
    [fn, delay],
  )
}

export default function AutocompleteDropdown({
  value,
  onChange,
  onSelect,
  fetchOptions,
  getOptionKey,
  getOptionLabel,
  debounceMs = 300,
  minChars = 2,
  inputProps = {},
}) {
  const [options, setOptions] = useState([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef(null)

  const fetchAndShow = useCallback(
    async (input) => {
      if (input.length < minChars) {
        setOptions([])
        setShowDropdown(false)
        return
      }
      const results = await fetchOptions(input)
      setOptions(results)
      setShowDropdown(results.length > 0)
      setHighlightedIndex(-1)
    },
    [fetchOptions, minChars],
  )

  const debouncedFetch = useDebounce(fetchAndShow, debounceMs)

  const handleChange = (e) => {
    onChange(e)
    debouncedFetch(e.target.value)
  }

  const handleSelect = (option) => {
    setShowDropdown(false)
    setOptions([])
    onSelect(option)
  }

  const handleKeyDown = (e) => {
    if (!showDropdown || options.length === 0) {
      inputProps.onKeyDown?.(e)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1))
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault()
      handleSelect(options[highlightedIndex])
    } else if (e.key === 'Escape') {
      setShowDropdown(false)
    } else {
      inputProps.onKeyDown?.(e)
    }
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <Box ref={containerRef} position="relative" w="full">
      <Input
        {...inputProps}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => options.length > 0 && setShowDropdown(true)}
        autoComplete="off"
      />
      {showDropdown && options.length > 0 && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={10}
          mt={1}
          bg={colors.grey900}
          border="1px solid"
          borderColor={colors.grey700}
          borderRadius="md"
          maxH="200px"
          overflowY="auto"
          boxShadow="lg"
        >
          {options.map((option, i) => (
            <Box
              key={getOptionKey(option)}
              px={3}
              py={2}
              cursor="pointer"
              fontSize="sm"
              color={colors.grey100}
              bg={i === highlightedIndex ? colors.grey700 : 'transparent'}
              _hover={{ bg: colors.grey700 }}
              onMouseDown={() => handleSelect(option)}
              onMouseEnter={() => setHighlightedIndex(i)}
            >
              {getOptionLabel(option)}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
